import { computed, getCurrentInstance, onMounted, onUnmounted, ref } from 'vue';
import { refresh } from '@/modules/cache';
import { canRefreshCache } from '@/utils/pwaUpdatePolicy';
import { useConnectionStatus } from '@/composables/useConnectionStatus';
import { useCollectStore } from '@/stores/collect';
import { genericEvent } from '@/utils/collectUpload';
import { logger } from '@/logging/logger';
import { LogCategory } from '@/logging/types';
import { useGeoStore } from '@/stores/geo';
import { useOfflineStore } from '@/stores/healthcheck';
import { useRegistrationStore } from '@/stores/registration';
import { usePreferenceStore } from '@/stores/preference';
import { connectionSpeedMbps, elapsedSince, estimatedDownloadTime } from '@/utils/deviceMetrics';
import type { ElapsedTime } from '@/utils/deviceMetrics';

const SYNC_QUEUE_NAME = 'workbox-background-sync:thinkmd-collect';
const SERVER_POLL_INTERVAL = 2000;

type BackgroundSyncRegistration = ServiceWorkerRegistration & {
  sync: { register: (tag: string) => void; getTags: () => Promise<string[]> };
};

type DeviceVueProxy = {
  recordsInQueue: () => Promise<number>;
  $sentry?: { captureException: (error: unknown) => void };
};

export function useDeviceStatus() {
  const proxy = getCurrentInstance()?.proxy as unknown as DeviceVueProxy;
  const collectStore = useCollectStore();
  const geoStore = useGeoStore();
  const offlineStore = useOfflineStore();
  const registrationStore = useRegistrationStore();
  const { online, serverAvailable, serverStatusToken } = useConnectionStatus();

  const syncActive = ref(false);
  const geoPermissionText = ref('responses.geo_permission.message.searching.title');
  const geoPermissionStatus = ref('');
  const pollTimer = ref<ReturnType<typeof setInterval> | null>(null);

  const records = computed<number>(() => collectStore.recordsInQueue);
  const queueStartedAt = computed(() => collectStore.queueStartedAt);
  const totalCompletedTracks = computed<number>(() => collectStore.track);
  const lastUpload = computed(() => collectStore.lastUploadDate);
  const timeSinceLastUpload = computed<ElapsedTime>(() => elapsedSince(lastUpload.value));

  const mbps = computed(connectionSpeedMbps);
  const estimatedTime = computed(() => estimatedDownloadTime(mbps.value));

  const refreshCacheBlocked = computed(() => !canRefreshCache());
  const cacheRefreshText = computed(() => online.value
    ? 'app.device.list.cache_refresh.online'
    : 'app.device.list.cache_refresh.offline');
  const cacheRefreshHint = computed(() => online.value && refreshCacheBlocked.value
    ? 'app.device.list.cache_refresh.in_progress'
    : null);
  const geoAcquired = computed(() => geoStore.acquired);
  const geoLocationCheckText = computed(() => geoStore.acquired
    ? 'app.device.list.location_check.ok'
    : 'app.device.list.location_check.error');
  const versionCheckText = computed(() => `app.device.list.version_check.${offlineStore.status}`);
  const versionCheck = computed(() => offlineStore.status === 'up_to_date');
  const offlineCheck = computed(() => offlineStore.readyForOffline);

  const badgeColor = (healthy: unknown): string => (healthy ? 'success' : 'caution');

  // One record per polling session: refreshSyncState reruns on every tick.
  let syncCheckReported = false;

  const stopPolling = (): void => {
    if (!pollTimer.value) return;
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  };

  const refreshSyncState = async (): Promise<void> => {
    try {
      const worker = await navigator.serviceWorker.ready as BackgroundSyncRegistration;
      const tags = await worker.sync.getTags();
      const queued = await proxy.recordsInQueue();
      syncActive.value = tags.includes(SYNC_QUEUE_NAME) && queued > 0;
    } catch (e) {
      if (syncCheckReported) return;
      syncCheckReported = true;

      logger.warn(
        LogCategory.SYNC,
        'sync.status.check.failed',
        { type: (e as Error)?.name },
        'Background sync status check failed',
      ).then();
    }
  };

  const startPolling = (): void => {
    if (pollTimer.value) return;

    // Reset on start, not stop: the stopping tick races its own un-awaited catch.
    syncCheckReported = false;

    pollTimer.value = setInterval(async () => {
      const available = await collectStore.checkIfServerIsAvailable();
      refreshSyncState();
      if (available) stopPolling();
    }, SERVER_POLL_INTERVAL);
  };

  const requestBackgroundSync = async (): Promise<void> => {
    syncActive.value = true;
    const worker = await navigator.serviceWorker.ready as BackgroundSyncRegistration;

    await genericEvent({
      name: 'uploadRecordsButtonClick',
      additions: ['globalSequence', 'encountersInQueue'],
    });

    try {
      worker.sync.register(SYNC_QUEUE_NAME);
    } catch (e) {
      proxy.$sentry?.captureException(e);

      let fallback = true;
      try {
        new BroadcastChannel('sw-messages').postMessage('SYNC_RECORDS');
      } catch (fallbackError) {
        fallback = false;
        proxy.$sentry?.captureException(fallbackError);
      }

      logger.error(
        LogCategory.SYNC,
        'sync.background.register.failed',
        { fallback, type: (e as Error)?.name },
        'Background sync registration failed',
      ).then();
    }

    startPolling();
  };

  const refreshCache = async (): Promise<void> => {
    try {
      await refresh();
    } catch (e) {
      proxy.$sentry?.captureException(e);
    }
  };

  const resetStatistics = (): void => {
    collectStore.resetQueue();
  };

  const loadGeoPermission = async (): Promise<void> => {
    const panel = usePreferenceStore().get('geo_permission');
    const message = panel.responses?.find((response: { name: string }) => response.name === 'geo_permission.message');
    const status = (message?.value as string) || 'error';
    geoPermissionStatus.value = status;
    geoPermissionText.value = `responses.geo_permission.message.${status}.title`;
  };

  onMounted(() => {
    refreshSyncState();
    loadGeoPermission();
    proxy.recordsInQueue();
    offlineStore.send();
    startPolling();
  });

  onUnmounted(stopPolling);

  return {
    online,
    connection: serverAvailable,
    connectionStatusText: serverStatusToken,
    cacheRefreshText,
    cacheRefreshHint,
    refreshCacheBlocked,
    records,
    mbps,
    estimatedTime,
    queueStartedAt,
    totalCompletedTracks,
    timeSinceLastUpload,
    geoAcquired,
    geoPermissionText,
    geoPermissionStatus,
    geoLocationCheckText,
    versionCheckText,
    versionCheck,
    offlineCheck,
    hrId: computed(() => registrationStore.hrId),
    syncActive,
    badgeColor,
    requestBackgroundSync,
    refreshCache,
    resetStatistics,
    startPolling,
  };
}
